import {
  CheckCircleOutlined,
  Edit,
  CancelOutlined as ErrorIcon,
} from '@mui/icons-material';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import { Typography as UITypography } from '@peritasai/ui-components';
import {
  componentTokens,
  themeColors as periverseColors,
  periverseTheme,
  themeShape,
} from '@peritasai/ui-shared';
import React, { useEffect, useRef, useState } from 'react';
import PickingTrayIcon from '../../../../assets/svg/pick-tray.svg';
import PickingCartIcon from '../../../../assets/svg/picking-cart.svg';

const DetectionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'state' && prop !== 'errorType',
})<{
  state: 'info' | 'detected' | 'error-assigned' | 'error-wrong-cart';
  errorType?: 'assigned' | 'wrong-cart';
}>(({ theme, state, errorType }) => {
  let backgroundColor: string;
  let borderColor: string;

  if (state === 'info') {
    backgroundColor = periverseColors.peritaspurple[50];
    borderColor = periverseColors.peritaspurple[500];
  } else if (state === 'detected') {
    backgroundColor = green[50]; // Light green
    borderColor = green[600];
  } else if (state === 'error-assigned' || errorType === 'assigned') {
    backgroundColor = componentTokens.components.alert.warning.background; // Orange/warning
    borderColor = componentTokens.warning.states.outlinedBorder;
  } else {
    backgroundColor = '#FFEBEE'; // Light red
    borderColor = red[600];
  }

  return {
    minHeight: '200px', // Fixed height
    borderRadius: `${themeShape.borderRadius.medium}px`, // borderRadius medium (16px)
    border: `1px solid ${borderColor}`,
    backgroundColor,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // padding: theme.spacing(2, 4),
    position: 'relative',
    cursor: state === 'info' ? 'pointer' : 'default',
    transition: 'background-color 0.3s ease',
  };
});

const InstructionImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '110px', // 6.9rem = 110.4px (using 16px base)
  flexShrink: 0,
  marginBottom: theme.spacing(1),
}));

const InstructionText = styled(UITypography)(({ theme }) => ({
  textAlign: 'center',
  fontSize: '1rem', // button/large font size from design
  lineHeight: '1.625rem', // button/large line height from design
  fontWeight: theme.typography.fontWeightMedium,
  letterSpacing: '0.0288rem', // button/large letter spacing from design
  textTransform: 'none', // Title case handled by content
}));

const PickingStationButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  textTransform: 'none',
  color: theme.palette.primary.dark,
  padding: 0,
  minWidth: 'auto',
  fontSize: '0.9375rem', // button/large font size from design
  lineHeight: '1.625rem', // button/large line height from design
  fontWeight: theme.typography.fontWeightMedium,
  letterSpacing: '0.0288rem', // button/large letter spacing from design
  '&:hover': {
    backgroundColor: 'rgba(35, 189, 232, 0.08)',
  },
}));

const StatusIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // marginBottom: theme.spacing(2),
  fontSize: '2.5rem',
}));

const ErrorMessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  textAlign: 'center',
}));

const ErrorTitle = styled(UITypography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  color: componentTokens.components.alert.error.color,
  // marginBottom: theme.spacing(1),
}));

const ErrorActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.shape.borderRadius,
}));

interface DetectionComponentProps {
  phase: 'cart' | 'tray';
  state: 'info' | 'detected' | 'error-assigned' | 'error-wrong-cart';
  detectionPhase?: 'cart' | 'tray'; // What was actually detected (for success view)
  pickingStation: string;
  onPickingStationEdit: () => void;
  onDetect: (isCorrectCart?: boolean) => void;
  detectedItem?: {
    id: string;
    name: string;
  };
  errorMessage?: string;
  errorType?: 'assigned' | 'wrong-cart';
  assignedCartName?: string; // Name of the cart that should be assigned (for wrong cart error)
  selectedCartId?: string; // ID of the currently selected cart (to check if cart is assigned)
  onOverride?: () => void;
  onRemove?: () => void;
  onDetectionSuccessComplete?: () => void;
}

export const DetectionComponent: React.FC<DetectionComponentProps> = ({
  phase,
  state,
  detectionPhase,
  pickingStation,
  onPickingStationEdit,
  onDetect,
  detectedItem,
  errorMessage,
  errorType,
  assignedCartName,
  selectedCartId,
  onOverride,
  onRemove,
  onDetectionSuccessComplete,
}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Handle 1-second timeout when detection is successful
  useEffect(() => {
    if (state === 'detected' && detectedItem && onDetectionSuccessComplete) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for 1 second
      timeoutRef.current = setTimeout(() => {
        onDetectionSuccessComplete();
      }, 1600);

      // Cleanup timeout on unmount or state change
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [state, detectedItem, onDetectionSuccessComplete]);

  const handleContainerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (state === 'info') {
      if (phase === 'cart') {
        // Show dropdown menu for cart phase
        setMenuAnchorEl(event.currentTarget);
      } else {
        // For tray phase, directly trigger detection
        onDetect();
      }
    }
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDetectCart = () => {
    handleMenuClose();
    onDetect(true);
  };

  const handleWrongCartDetected = () => {
    handleMenuClose();
    onDetect(false);
  };

  const getInstructionText = () => {
    if (phase === 'cart') {
      // If cart is assigned (has selectedCartId), show cart name; otherwise show "Empty Cart"
      if (selectedCartId && assignedCartName) {
        return `Move Cart ${assignedCartName} onto`;
      }
      return 'Move Empty Cart onto';
    } else {
      return 'Place Trays onto Cart';
    }
  };

  const getDetectedItemName = () => {
    return detectedItem?.name || detectedItem?.id || '';
  };

  const getAssignmentMessage = () => {
    // Use detectionPhase for success view, fallback to phase for error view
    const displayPhase = detectionPhase || phase;
    return displayPhase === 'cart' ? 'Cart Assigned' : 'Tray Assigned';
  };

  const isErrorState =
    state === 'error-assigned' || state === 'error-wrong-cart';
  const isAssignedError =
    state === 'error-assigned' || errorType === 'assigned';
  const isWrongCartError =
    state === 'error-wrong-cart' || errorType === 'wrong-cart';

  return (
    <>
      <DetectionContainer
        state={state}
        errorType={errorType}
        onClick={handleContainerClick}
      >
        {state === 'info' && (
          <>
            <InstructionImageContainer>
              <img
                src={phase === 'cart' ? PickingCartIcon : PickingTrayIcon}
                alt={phase === 'cart' ? 'Move Cart' : 'Move Tray'}
                height="110px"
                width="140px"
                style={{ display: 'block' }}
              />
            </InstructionImageContainer>
            <InstructionText
              sx={{
                color: periverseColors.peritasgrey[800],
                fontSize: '16px',
                lineHeight: 1.33,
                fontWeight: 600,
                letterSpacing: '0px',
              }}
            >
              {getInstructionText()}
            </InstructionText>
            {phase === 'cart' && (
              <PickingStationButton
                onClick={(e) => {
                  e.stopPropagation();
                  onPickingStationEdit();
                }}
                endIcon={<Edit sx={{ fontSize: 16 }} />}
              >
                {pickingStation}
              </PickingStationButton>
            )}
          </>
        )}

        {state === 'detected' && (
          <>
            <StatusIconContainer sx={{ mb: periverseTheme.spacing(1) }}>
              <CheckCircleOutlined
                sx={{
                  fontSize: 40,
                  color: periverseTheme.palette.success.main,
                }}
              />
            </StatusIconContainer>
            <InstructionText
              variant="body1"
              sx={{
                mb: periverseTheme.spacing(1),
                fontWeight: 500,
                lineHeight: 1.33,
              }}
            >
              {getDetectedItemName()}
            </InstructionText>
            <InstructionText
              variant="body1"
              sx={{
                fontColor: periverseColors.peritasgrey[800],
                fontWeight: 400,
                lineHeight: 1.33,
              }}
            >
              {getAssignmentMessage()}
            </InstructionText>
          </>
        )}

        {isErrorState && (
          <ErrorMessageContainer>
            <StatusIconContainer>
              <ErrorIcon
                sx={{
                  fontSize: 40,
                  color: isAssignedError
                    ? componentTokens.warning.states.outlinedBorder
                    : red[600],
                }}
              />
            </StatusIconContainer>
            <ErrorTitle
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                color: isAssignedError
                  ? componentTokens.components.alert.warning.color
                  : componentTokens.components.alert.error.color,
              }}
            >
              {isWrongCartError
                ? 'Incorrect Cart On Picking Station'
                : phase === 'cart'
                ? 'Cart Assigned to Different Case'
                : 'Tray Assigned to Different Case'}
            </ErrorTitle>
            <UITypography
              variant="body2"
              sx={{
                color: isAssignedError
                  ? componentTokens.components.alert.warning.color
                  : componentTokens.components.alert.error.color,
                maxWidth: '664px',
              }}
            >
              {isWrongCartError ? (
                <>
                  Cart <strong>{assignedCartName || ''}</strong> is assigned to
                  this case, but Cart{' '}
                  <strong>{detectedItem?.name || 'CC-ORTHO-002'}</strong> was
                  detected. Select <strong>Remove Cart</strong> to detect the
                  correct cart. Alternatively select{' '}
                  <strong>Override Cart</strong> to change assignment.
                </>
              ) : (
                <>
                  {`${phase === 'cart' ? 'Cart' : 'Tray'} `}
                  <strong>{detectedItem?.name || ''}</strong>
                  {` was detected, this ${
                    phase === 'cart' ? 'cart' : 'tray'
                  } is assigned to another case. Select `}
                  <strong>{`Remove ${
                    phase === 'cart' ? 'Cart' : 'Tray'
                  }`}</strong>
                  {` and follow instructions or select `}
                  <strong>{`Override ${
                    phase === 'cart' ? 'Cart' : 'Tray'
                  }`}</strong>
                  {` to edit assignment.`}
                </>
              )}
            </UITypography>
            <ErrorActionsContainer>
              {onOverride && (
                <ActionButton
                  variant="outlined"
                  onClick={onOverride}
                  sx={{
                    borderColor: componentTokens.primary.states.outlinedBorder,
                    color: periverseTheme.palette.primary.dark,
                    '&:hover': {
                      borderColor: periverseColors.peritaspurple[600],
                      backgroundColor: 'rgba(35, 189, 232, 0.04)',
                    },
                  }}
                >
                  Override {phase === 'cart' ? 'Cart' : 'Tray'}
                </ActionButton>
              )}
              {onRemove && (
                <ActionButton
                  variant="contained"
                  onClick={onRemove}
                  sx={{
                    backgroundColor: periverseTheme.palette.primary.main,
                    color: periverseTheme.palette.common.white,
                    '&:hover': {
                      backgroundColor: periverseColors.peritaspurple[400],
                    },
                  }}
                >
                  Remove {phase === 'cart' ? 'Cart' : 'Tray'}
                </ActionButton>
              )}
            </ErrorActionsContainer>
          </ErrorMessageContainer>
        )}
      </DetectionContainer>

      {/* Dropdown menu for cart detection options */}
      {phase === 'cart' && (
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem onClick={handleDetectCart}>Detect cart</MenuItem>
          <MenuItem onClick={handleWrongCartDetected}>
            Wrong cart detected
          </MenuItem>
        </Menu>
      )}
    </>
  );
};
